import React, { useCallback, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { pdfjs, Document, Page, Outline } from "react-pdf";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Stage, Layer, Rect, Text, Group, Circle, Shape } from "react-konva";
import Konva from "konva";
import clsx from "clsx";

import { useModalsState, useModals, CLOSE_PREVIEW } from "../context/modals";
import {
  useEditor,
  UPDATE_SIGNATURE,
  REMOVE_SIGNATURE,
} from "../context/editor";

import SignatureElement from "./DocumentPreview/TratoOldFormat/SignatureElement";
import HeaderElement from "./HeaderElement";

import { getDisplayName } from "../utils";

function renderElements(
  elements,
  isPremium,
  { elementSelected, jurisdiction, participants, isDynamic }
) {
  let charactersLength = 0;
  let hasThreePoint = false;
  if (!elements || !elements.length) {
    return null;
  }
  let statementsIndex = 0;
  let clausesIndex = 0;

  return elements.map((element, i) => {
    charactersLength += element.content?.length ?? 0;

    if (charactersLength > 9000 && isPremium) {
      if (!hasThreePoint) {
        hasThreePoint = true;
        return <div className="text-center">....</div>;
      }
      return null;
    }

    if (!element?.content?.length) {
      return null;
    }
    if (element.type === "clause") {
      clausesIndex++;
    } else if (element.type === "statement") {
      statementsIndex++;
    }

    return (
      <ContractElement
        key={element._id}
        index={i}
        clausesIndex={clausesIndex}
        statementsIndex={statementsIndex}
        isContractDynamic={isDynamic}
        isSelected={elementSelected === element._id}
        participants={participants}
        jurisdiction={jurisdiction}
        {...element}
      />
    );
  });
}

export function ContractPreviewTrato({
  contract,
  card,
  header,
  elementSelected,
  participants,
}) {
  const { t } = useTranslation("common");
  const elements = useMemo(() => {
    // contract?.previewType === 'premium'
    return (
      <div className="p-4 elements-container" style={{ minWidth: 500 }}>
        {renderElements(
          contract.elements,
          ["premium", "free"].includes(contract?.previewType),
          {
            elementSelected,
            participants: participants ?? contract.participants,
            jurisdiction: contract.jurisdiction,
            isDynamic: contract.isDynamic,
          }
        )}
      </div>
    );
  }, [contract, elementSelected, participants]);

  if (card) {
    return (
      <Container
        fluid
        className={clsx("d-flex justify-content-center")}
        style={{ width: 700, maxHeight: 800 }}
      >
        <Card className={clsx("my-3 shadow-sm")}>
          {header && (
            <CardHeader>
              <h4 className="font-weight-bold mb-0">{contract.name}</h4>
              <p className="text-muted mb-0">{t("preview")}</p>
            </CardHeader>
          )}
          <CardBody
            className={clsx("contract-preview overflow-auto ")}
            style={{ minHeight: 500 }}
          >
            {elements}
          </CardBody>
        </Card>
      </Container>
    );
  }

  return <>{elements}</>;
}

function ContractPreviewContainer({ card, width, header = true }) {
  const { t } = useTranslation("common");
  const [editorState, editorDispatch] = useEditor();
  const {
    document: doc,
    isPdf,
    isEditable,
    currentPage,
    participants,
    signatures,
  } = editorState;

  const handleDragStart = useCallback((e) => {
    e.target.setAttrs({
      shadowOffset: {
        x: 15,
        y: 15,
      },
      scaleX: 1,
      scaleY: 1,
    });
  }, []);

  const handleDragEnd = (e) => {
    const signature = {
      _id: e.target.attrs.id,
      left: e.target.attrs.x,
      top: e.target.attrs.y,
      width: 595,
      height: 842,
      page: editorState.currentPage,
    };

    editorDispatch({
      type: UPDATE_SIGNATURE,
      participantId: e.target.attrs.participantId,
      signature,
    });

    e.target.to({
      duration: 0.5,
      easing: Konva.Easings.ElasticEaseOut,
      scaleX: 1,
      scaleY: 1,
      shadowOffsetX: 5,
      shadowOffsetY: 5,
    });
  };

  const handleDragBound = (pos, event) => {
    const height = 842;
    const width = 595;
    const newY = pos.y < 15 ? 15 : pos.y > height - 104 ? height - 104 : pos.y;
    const newX = pos.x < 1 ? 1 : pos.x > width - 205 ? width - 205 : pos.x;

    return {
      x: newX,
      y: newY,
    };
  };

  const addSignature = (participant) => () => {
    editorDispatch({ type: "add-signature", participantId: participant._id });
  };

  const handleRemoveSignature = (e) => {
    const signatureId = e.target.attrs.signatureId;
    const participantId = e.target.attrs.participantId;
    editorDispatch({ type: REMOVE_SIGNATURE, participantId, signatureId });
  };

  if (card) {
    return (
      <>
        {/* {isPdf && isEditable && <UncontrolledButtonDropdown className={clsx("float-right", !isEditable && 'disabled')} direction="down" style={{zIndex: 10000}} disabled={!isEditable}>
                    <DropdownToggle caret color="accent-100" size="sm">
                        Agregar firmas
                    </DropdownToggle>
                    <DropdownMenu>
                        {
                        participants?.map((participant, i)  => (
                        <React.Fragment key={participant._id}> 
                            {i !== 0 && <DropdownItem divider /> }
                            <DropdownItem onClick={addSignature(participant)}>{participant.label}</DropdownItem>
                            
                        </React.Fragment>
                        ))
                        }
                    </DropdownMenu>
                </UncontrolledButtonDropdown>  }   */}
        <Container
          fluid
          className={clsx("d-flex justify-content-center")}
          style={{ width: 700, maxHeight: 900 }}
        >
          <Card className={clsx("my-3 shadow-sm", isPdf && "mw-700")}>
            {header && (
              <CardHeader>
                <h4 className="font-weight-bold mb-0">{doc.name}</h4>
                <p className="text-muted mb-0">{t("preview")}</p>
              </CardHeader>
            )}
            <CardBody
              className={clsx(
                "contract-preview overflow-auto ",
                isPdf && "p-0"
              )}
              style={{ minHeight: 500 }}
            >
              {isPdf ? (
                <ContractPreviewPdf
                  contract={doc}
                  url={doc.importedPdf}
                  editable={isEditable}
                  signatures={signatures}
                  width={width}
                  participants={participants}
                  currentPage={currentPage}
                  onDragStart={handleDragStart}
                  onDragBound={handleDragBound}
                  onRemoveSignature={handleRemoveSignature}
                  onDragEnd={handleDragEnd}
                />
              ) : (
                <ContractPreviewTrato
                  contract={doc}
                  width={width}
                  participants={participants}
                />
              )}
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      {isPdf ? (
        <ContractPreviewPdf
          contract={doc}
          url={doc.importedPdf}
          editable={isEditable}
          signatures={signatures}
          width={width}
          currentPage={currentPage}
          onDragStart={handleDragStart}
          onDragBound={handleDragBound}
          onRemoveSignature={handleRemoveSignature}
          onDragEnd={handleDragEnd}
        />
      ) : (
        {
          /*<ContractPreviewTrato contract={doc} header={header} card={card} participants={participants} />*/
        }
      )}
    </>
  );
}

export function ContractPreviewModal({ editable }) {
  const [modalsState, dispatch] = useModals();
  const { t } = useTranslation("common");
  const [numPages, setNumPages] = useState(1);

  const handleClose = useCallback(() => {
    dispatch({ type: CLOSE_PREVIEW });
  }, [dispatch]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  if (!modalsState.preview) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      id="contract-preview"
      className={clsx(
        "position-fixed bg-light border rounded",
        modalsState.center && "center",
        modalsState.template?.pluginType === "pdf" && "overflow-auto"
      )}
    >
      <div className="bg-grey p-4 border-bottom rounded-top">
        <div className="row">
          <div className="col-md-8 col-8">
            <h5 className="font-weight-bold mb-0">
              {modalsState.template.name}
            </h5>
            <p className="mb-0 text-muted">{t("preview")}</p>
          </div>
          <div className="col-md-4 col-4">
            <div
              className="btn btn-primary btn-sm pointer float-right"
              onClick={handleClose}
            >
              {t("actions.close")}
              <FontAwesomeIcon icon={"times-circle"} className="ml-2" inverse />
            </div>
          </div>
        </div>
      </div>
      {modalsState.template?.pluginType !== "pdf" && (
        <ContractPreviewTrato contract={modalsState.template} />
      )}
      {modalsState.template?.pluginType === "pdf" && (
        <Document
          file={modalsState.template.importedPdf}
          onLoadSuccess={onDocumentLoadSuccess}
          options={{
    cMapUrl: `cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: 'standard_fonts/',
}}
          style={
            {
              // width: '200px',
            }
          }
        >
          {Array.from(new Array(numPages), (el, index) => (
            <div>
              <Page
                className="my-3"
                key={`page_${index + 1}`}
                width={600}
                pageNumber={index + 1}
              />
            </div>
          ))}
        </Document>
      )}
    </div>,
    document.body
  );
}

export function ContractPreviewPdf({
  contract,
  participants,
  url,
  width,
  editable,
  currentPage,
  offset = 0,
  signatures,
  onDragStart,
  onDragEnd,
  onDragBound,
  onRemoveSignature,
}) {
  const [numPages, setNumPages] = useState(1);
  const [status, setStatus] = useState("");
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setStatus("document-loaded");
  };

  return (
    <div className="document-container d-flex flex-column align-items-center">
      {" "}
      {/*p-2 mt-3 */}
      <Stage width={595} height={842} className="layer" id="layer">
        <Layer>
          {status === "document-loaded" &&
            participants?.map((participant) =>
              signatures[participant._id]?.map((signature, i) => {
                if (currentPage && currentPage !== signature.page) {
                  return null;
                }

                return (
                  <Group
                    key={i}
                    x={signature.left}
                    y={signature.top}
                    id={signature._id}
                    width={signature.width}
                    height={signature.height}
                    participantId={participant._id}
                    draggable={editable}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    dragBoundFunc={onDragBound}
                  >
                    <Text
                      fontSize={18}
                      offsetY={-100 / 2.5}
                      width={200}
                      height={100}
                      text={`Área de firma \n ${participant.label}`}
                      align="center"
                    />
                    <Rect
                      width={200}
                      height={100}
                      fill="black"
                      opacity={0.3}
                      shadowEnabled={false}
                    />
                    {editable && (
                      <Circle
                        participantId={participant._id}
                        onClick={onRemoveSignature}
                        signatureId={signature._id}
                        x={200}
                        fill="red"
                        radius={7}
                      />
                    )}
                  </Group>
                );
              })
            )}
        </Layer>
      </Stage>
      <div
        className="custom-pdf"
        style={{
          top: `-842px`,
          width: `595px`,
          marginBottom: `-842px`,
        }}
      >
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          style={
            {
              // width: '200px',
            }
          }
        >
          <div>
            <Page className="my-3" width={width} pageNumber={currentPage} />
          </div>
        </Document>
      </div>
    </div>
  );
}

function ContractElement({
  type,
  content = "",
  number,
  index,
  clausesIndex,
  statementsIndex,
  staticfield,
  isSelected,
  isContractDynamic,
  participants,
  jurisdiction,
}) {
  if (type === "break") {
    return <div className="page-break"></div>;
  }

  const getOrdinalNumber = (number) => {
    if (!isNaN(number)) {
      const num = String(number);
      const units = [
        "primera",
        "segunda",
        "tercera",
        "cuarta",
        "quinta",
        "sexta",
        "séptima",
        "octava",
        "novena",
      ];
      const dozens = [
        "décima",
        "vigésima",
        "trigésima",
        "cuadragésima",
        "quincuagésima",
        "sexagésima",
        "septuagésima",
        "octogésima",
        "nonagésima",
      ];
      const hundreds = [
        "centésima",
        "ducentésima",
        "tricentésima",
        "cuadringentésima",
        "quingentésima",
        "sexcentésima",
        "septingentésima",
        "octingentésima",
        "noningentésima",
      ];
      const array = [units, dozens, hundreds];
      const word = [];
      let index = 0;
      for (let i = num.length; i > 0; i--) {
        const ordinalText = array[index][Number(num[i - 1]) - 1];
        if (ordinalText) {
          word.unshift(ordinalText);
        }

        index++;
      }

      return word?.join(" ")?.toUpperCase();
    }

    return "";
  };

  const renderLogo = () => {
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  };

  const renderHeader = (isStatic) => {
    return (
      <div className="header-text contenteditable">
        <HeaderElement
          participants={participants}
          type={type}
          isStatic={isStatic}
        >
          <div dangerouslySetInnerHTML={{ __html: content }} />
        </HeaderElement>
      </div>
    );
  };
  const renderTitle = () => {
    return (
      <div className="simple-title contenteditable">
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    );
  };

  const renderText = () => {
    return (
      <div className="simple-text contenteditable">
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    );
  };

  const renderTable = () => {
    return (
      <div className="simple-text contenteditable">
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    );
  };

  const renderStatement = () => {
    return (
      <div className="statement-text contenteditable">
        <div className="statement-title">
          {" "}
          {isContractDynamic ? statementsIndex : number ? number : index}.
        </div>
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    );
  };

  const renderClause = () => {
    return (
      <div className="clause-text contenteditable">
        <div className="clause-title">
          {isContractDynamic
            ? getOrdinalNumber(clausesIndex)
            : number
            ? getOrdinalNumber(number)
            : getOrdinalNumber(index)}
          .
        </div>
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    );
  };

  const renderDocumentFooter = () => {
    return (
      <div id="pageFooter">
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    );
  };

  const renderDocumentHeader = () => {
    return (
      <div id="pageHeader">
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    );
  };

  const renderSignature = () => {
    return (
      <>
        <div className="signature-content">
          {participants &&
            participants?.map((participant) => (
              <React.Fragment key={participant._id}>
                {(content.includes(participant._id) ||
                  participant.isNewParticipant) && (
                  <SignatureElement
                    participantId={participant._id}
                    label={participant.label}
                    name={
                      participant.obligation === "legal"
                        ? getDisplayName(participant.name)
                        : getDisplayName(
                            participant.name,
                            participant.last_name
                          )
                    }
                    representative={participant.representative}
                    obligation={participant.obligation}
                    jurisdiction={jurisdiction ?? "mx"}
                  />
                )}
              </React.Fragment>
            ))}
        </div>
      </>
    );
  };

  return (
    <div className={clsx("element px-1", isSelected && "border border-accent")}>
      <div className="element-content">
        {/* {type === 'staticheader' && renderHeader(staticfield)}  */}
        {type === "header" && renderHeader(staticfield)}
        {type === "document_logo" && renderLogo()}
        {type === "title" && renderTitle()}
        {type === "text" && renderText()}
        {type === "table" && renderTable()}
        {type === "statement" && renderStatement()}
        {type === "clause" && renderClause()}
        {type === "signature" && renderSignature()}
        {type === "document_footer" && renderDocumentFooter()}
        {type === "document_header" && renderDocumentHeader()}
      </div>
    </div>
  );
}

export default ContractPreviewContainer;
